import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import PhotoUpload from "@/components/PhotoUpload";
import { toast } from "sonner";
import { KeyRound, LogOut, GraduationCap, Trash2, User, BellRing } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { KitchenPinCard } from "@/components/tablet/KitchenPinCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const avatarSrc = (url) => (url ? `${BACKEND}${url}?auth=${localStorage.getItem("cc_token") || ""}` : undefined);

export default function Profile() {
  const { logout, setUser, user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [nickname, setNickname] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/profile/me");
    setProfile(data);
    setNickname(data.nickname || "");
  };
  useEffect(() => { load(); }, []);

  const saveNickname = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile/me", { nickname: nickname.trim() || null });
      setProfile((p) => ({ ...p, ...data }));
      toast.success("Display name updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update display name"); }
    finally { setSaving(false); }
  };

  const onAvatar = async (fileId) => {
    if (!fileId) return;
    try {
      const { data } = await api.post("/profile/me/avatar", { file_id: fileId });
      setProfile((p) => ({ ...p, ...data }));
      if (user) setUser({ ...user, avatar: avatarSrc(data.avatar_url) });
      toast.success("Profile photo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set profile photo"); }
  };
  const removeAvatar = async () => {
    try {
      const { data } = await api.delete("/profile/me/avatar");
      setProfile((p) => ({ ...p, ...data }));
      if (user) setUser({ ...user, avatar: null });
      toast.success("Profile photo removed");
    } catch { toast.error("Could not remove photo"); }
  };

  const toggleClockReminders = async (enabled) => {
    setProfile((p) => ({ ...p, clock_reminders_enabled: enabled }));
    try {
      const { data } = await api.put("/profile/me/preferences", { clock_reminders_enabled: enabled });
      setProfile((p) => ({ ...p, ...data }));
      toast.success(enabled ? "Clock-out reminders on" : "Clock-out reminders off");
    } catch (e) {
      setProfile((p) => ({ ...p, clock_reminders_enabled: !enabled }));
      toast.error(e.response?.data?.detail || "Could not update reminder setting");
    }
  };

  if (!profile) return null;

  return (
    <div data-testid="profile-page">
      <PageHeader title="My Profile" subtitle="Your display name and photo — how the team sees you. This is separate from your account and records." testid="profile-header" />

      <div className="cc-card p-5 mb-5 flex items-center gap-4" data-testid="profile-identity">
        <Avatar className="h-16 w-16 border border-slate-200">
          <AvatarImage src={avatarSrc(profile.avatar_url)} alt={profile.display_name} />
          <AvatarFallback className="bg-brand-light text-brand-dark text-lg font-bold">{profile.initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="text-lg font-bold text-slate-800 truncate" data-testid="profile-display-name">{profile.display_name}</div>
          <div className="text-xs text-slate-400 capitalize">{profile.role} · {profile.real_name}</div>
        </div>
      </div>

      <div className="cc-card p-5 mb-5 space-y-3" data-testid="profile-avatar-section">
        <Label className="text-sm font-semibold text-slate-700">Profile photo</Label>
        <PhotoUpload value={profile.avatar_file_id} onChange={onAvatar} label="Upload a profile photo" testid="profile-avatar-upload" />
        {profile.has_avatar && <Button variant="outline" size="sm" onClick={removeAvatar} data-testid="profile-avatar-remove" className="text-red-600"><Trash2 size={14} className="mr-1" /> Remove photo</Button>}
      </div>

      <div className="cc-card p-5 mb-5 space-y-3" data-testid="profile-nickname-section">
        <Label className="text-sm font-semibold text-slate-700" htmlFor="nickname-input">Display name (nickname)</Label>
        <p className="text-xs text-slate-400">Optional. 2–30 characters. Shown on your tasks and around CaterCTRL. Your real name is always kept for records.</p>
        <div className="flex gap-2">
          <Input id="nickname-input" value={nickname} onChange={(e) => setNickname(e.target.value)} data-testid="profile-nickname-input" placeholder={profile.real_name} className="flex-1" maxLength={30} />
          <Button onClick={saveNickname} disabled={saving} data-testid="profile-nickname-save" className="bg-brand hover:bg-brand-hover">Save</Button>
        </div>
      </div>

      <KitchenPinCard />
      <div className="cc-card p-5 mb-5 space-y-3" data-testid="profile-notifications-section">
        <Label className="text-sm font-semibold text-slate-700">Notifications</Label>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><BellRing size={15} className="text-brand" /> Clock-out reminders</div>
            <p className="text-xs text-slate-400 mt-0.5">If you're still clocked in after your scheduled shift end, we'll send you a reminder to clock out.</p>
          </div>
          <Switch checked={!!profile.clock_reminders_enabled} onCheckedChange={toggleClockReminders} data-testid="profile-clock-reminders-toggle" />
        </div>
      </div>

      <div className="cc-card p-2 divide-y divide-slate-100" data-testid="profile-actions">
        <button onClick={() => { window.dispatchEvent(new Event("cc-replay-tutorial")); }} data-testid="profile-take-tour" className="w-full flex items-center gap-3 px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">
          <GraduationCap size={18} className="text-brand" /> Take the CaterCTRL tour again
        </button>
        <button onClick={() => navigate("/change-password")} data-testid="profile-change-password" className="w-full flex items-center gap-3 px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">
          <KeyRound size={18} /> Change password
        </button>
        <button onClick={() => { logout(); navigate("/login"); }} data-testid="profile-sign-out" className="w-full flex items-center gap-3 px-3 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg">
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );
}
